import React, { useContext } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { SettingsContext } from "../../contexts";
import { Button, Checkbox, Icon, IconButton } from "../../components/ui";
import { getFileUrl } from "../../core/config/api.js";
import { formatRequestId } from "../../order/helpers";
import { requestMetaLine } from "./requestSummary";
import { useRequestLookbook } from "./useRequestLookbook";

const SortableSelectedRequest = ({ id, request, position }) => {
  const settings = useContext(SettingsContext);
  const { remove } = useRequestLookbook();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const meta = requestMetaLine(request);
  const name = request?.product?.brand?.name ?? `request ${id}`;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`rlb-tray__row${isDragging ? " rlb-tray__row--dragging" : ""}`}
    >
      {/* The handle is the only drag target: the row also carries a checkbox and
          a remove button, and a whole-row listener swallows their clicks. */}
      <IconButton
        variant="ghost"
        size="sm"
        className="rlb-tray__handle"
        label={`Reorder ${name}`}
        {...attributes}
        {...listeners}
      >
        <Icon name="GripVertical" size="xs" />
      </IconButton>

      <span className="rlb-tray__position">{position}</span>

      <Checkbox
        checked
        onChange={() => remove(id)}
        aria-label={`Remove ${name} from the lookbook`}
      />

      <img
        className="rlb-tray__image"
        src={getFileUrl(request?.product?.images?.[0]?.id)}
        alt=""
      />

      <div className="rlb-tray__text">
        <span className="rlb-tray__brand">{request?.product?.brand?.name}</span>
        <span className="rlb-tray__model">{request?.product?.model?.name}</span>
        {meta && <span className="rlb-tray__meta">{meta}</span>}
      </div>

      <span className="rlb-tray__id">
        {request?.request_type
          ? formatRequestId(settings.settings, request)
          : `#${id}`}
      </span>

      <Button
        variant="ghost"
        size="sm"
        className="rlb-tray__remove"
        onClick={() => remove(id)}
      >
        Remove
      </Button>
    </li>
  );
};

/**
 * The picked requests, pinned above the list in the order they will appear in
 * the PDF. Reordering here is the only thing that sets that order — it has no
 * effect on the Orders page itself.
 */
export const SelectedRequestsTray = () => {
  const { order, setOrder, selected, count } = useRequestLookbook();

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    setOrder((items) =>
      arrayMove(items, items.indexOf(active.id), items.indexOf(over.id))
    );
  };

  if (count === 0) {
    return (
      <div className="rlb-tray rlb-tray--empty">
        <p className="rlb-tray__empty-title">No requests selected yet</p>
        <p className="rlb-tray__empty-body">
          Tick a request below to add it. Selected requests move up here, where
          you can drag them into the order you want in the PDF.
        </p>
      </div>
    );
  }

  return (
    <div className="rlb-tray">
      <div className="rlb-tray__header">
        <span className="rlb-tray__title">In this lookbook · {count}</span>
        <span className="rlb-tray__hint">Drag to reorder</span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={order} strategy={verticalListSortingStrategy}>
          <ul className="rlb-tray__list">
            {order.map((id, index) => {
              const request = selected[id];
              // An id can outlive its object while the selection settles — skip
              // rather than dereference `request.product` and take the list down.
              if (!request) return null;

              return (
                <SortableSelectedRequest
                  key={id}
                  id={id}
                  request={request}
                  position={index + 1}
                />
              );
            })}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default SelectedRequestsTray;
